import { cn } from "@/utils";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { Star } from "lucide-react";
import { useMemo } from "react";
import { classifyTransfer } from "./classifyTransfer";
import { asDraggableData, type DroppableData, droppableId } from "./ids";
import { transferDropTargetFavoritesActiveClassName } from "./transferDropTargetStyles";
import type { FavoritesResourceType } from "./types";

/**
 * Wraps a left-rail Favorites section as a drop target. Dropping a
 * favoritable row here stars it — the drag-equivalent of each row's
 * Star menu item. Highlights only when the active drag carries something
 * the scoped resource type accepts.
 */
export function RailFavoritesDropTarget({
  resourceType,
  children,
  className,
}: {
  resourceType: FavoritesResourceType;
  children: React.ReactNode;
  className?: string;
}) {
  const dropId = droppableId.railFavorites(resourceType);
  const { active, over } = useDndContext();

  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "rail-favorites",
      destination: { kind: "favorites", resourceType },
    }),
    [resourceType],
  );

  const droppable = useDroppable({ id: dropId, data: droppableData });

  const draggable = active ? asDraggableData(active.data.current) : null;
  const hasFavoritable =
    draggable !== null &&
    classifyTransfer(draggable, { kind: "favorites", resourceType }).kind ===
      "favorite";

  const showHighlight = over?.id === dropId && hasFavoritable;

  return (
    <div
      ref={droppable.setNodeRef}
      className={cn(
        "relative min-h-0 rounded-xl transition-colors duration-150",
        showHighlight && transferDropTargetFavoritesActiveClassName,
        className,
      )}
    >
      {showHighlight ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl"
        >
          <Star className="size-7 fill-amber-500/20 text-amber-500/35" />
        </div>
      ) : null}
      {children}
    </div>
  );
}
